import React, { FC, useCallback, useEffect } from 'react';

import { useStoreContext } from 'features/store';
import { useIntl } from 'react-intl';

import { ILocation } from '@rbi-ctg/frontend';
import { AutoPromptLocationPermission } from 'components/auto-prompt-location-permission';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { ClickEventComponentNames, CustomEventNames, logButtonClick } from 'state/amplitude';
import { useGeolocation } from 'state/geolocation';
import { actions, useAppDispatch } from 'state/global-state';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { useScrollViewFullHeight } from 'state/scroll/hooks';
import { useServiceModeContext } from 'state/service-mode';
import { EventName, emitEvent } from 'utils/event-hub';
import { isValidPosition } from 'utils/geolocation';
import { routes } from 'utils/routing';
import { ServiceModeCategory } from 'utils/service-mode';

import { useSearchRestaurants } from './hooks/use-search-restaurants';
import { useServiceModeCategory } from './hooks/use-service-mode-category';
import useStoreLocatorView from './hooks/use-store-locator-view';
import { useStoresReducer } from './hooks/use-stores-reducer';
import { StoreLocatorView } from './store-locator.view';

/**
 *
 * StoreLocator purpose
 *
 */
export const StoreLocator: FC<React.PropsWithChildren<{
  isInSupportForm?: boolean;
  handleSelectStore?: (storeId: string) => void;
  onDismissInSupportForm?: () => void;
}>> = ({ isInSupportForm, handleSelectStore, onDismissInSupportForm, ...rest }) => {
  const isDesktop = useIsDesktopBreakpoint();
  const { formatMessage } = useIntl();
  const orderingEnabled = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const {
    noStoreSelected,
    StoreChangeDialog,
    onConfirmStoreChange,
    onConfirmStoreChangeParams,
    clearOnConfirmStoreChangeParams,
    store,
  } = useStoreContext();

  const { updateUserStore } = useLDContext();

  const {
    reorder: { resetPendingReorder },
    recent: { setPendingRecentItem },
  } = useOrderContext();

  useScrollViewFullHeight();

  // There is a race condition when we reorder an item and is not available for delivery
  // adding this timeout fix the issue bc gives the time to make the updates on the context that needs the store
  // Reset pending Reorders/Recent items if no store selected on unmount
  useEffectOnUnmount(() => {
    setTimeout(() => {
      if (noStoreSelected) {
        resetPendingReorder();
        setPendingRecentItem(null);
      }
    }, 1500);
  });

  const {
    errorFavs,
    errorNearby,
    errorRecent,
    searchingNearby,
    searchingRecent,
    searchingFavs,
    storesNearby,
    storesFavs,
    storesRecent,
    rawIsSearching,
    storeLocatorDispatch,
  } = useStoresReducer();
  const { serviceMode, setServiceMode } = useServiceModeContext();

  const {
    activeCoordinates,
    manualAddress,
    isPermissionGranted,
    resetCoordsAndLoadCurrentPosition,
    setCoordinatesManual,
    setManualAddress,
    updateManualCoordinates,
  } = useGeolocation();

  const {
    ErrorDialog,
    Dialog,
    searchFavRestaurants,
    searchNearbyRestaurants,
    searchRecentRestaurants,
  } = useSearchRestaurants({
    storeLocatorDispatch,
  });

  const { currentView, updateStoreLocatorView } = useStoreLocatorView();

  const {
    serviceModeCategory,
    setServiceModeCategory,
    serviceModeCategoryOptions,
  } = useServiceModeCategory();

  const onServiceModeCategoryChange = useCallback(
    (value: ServiceModeCategory) => {
      if (serviceModeCategory === value) {
        return;
      }

      // TODO: this dispatch looks like a bug - we should call this when a new store is selected
      // Switching tabs on the UI should not trigger this refetch
      // Set shouldRefetchOffers to true when changing service mode
      dispatch(actions.loyalty.setShouldRefetchOffers(true));

      logButtonClick({
        attributes: {
          'Service Mode': value,
          component: ClickEventComponentNames.BUTTON,
          Name: CustomEventNames.BUTTON_CLICK_SERVICE_MODE_TOGGLE,
          name: 'Service Mode Toggle Button',
        },
      });

      switch (value) {
        case ServiceModeCategory.PICKUP:
          setServiceModeCategory(value);
          navigate(routes.storeLocator);
          setServiceMode(null);
          break;
        case ServiceModeCategory.DELIVERY:
          navigate(routes.deliveryAddress);
          break;
        default:
          break;
      }
    },
    [navigate, serviceModeCategory, setServiceMode, setServiceModeCategory, dispatch]
  );

  const onPressSearch = useCallback(
    (location: ILocation) => {
      setCoordinatesManual({ newCoords: location });
      setManualAddress('');
    },
    [setCoordinatesManual, setManualAddress]
  );

  const handleOnConfirmCb = () => {
    onConfirmStoreChange(onConfirmStoreChangeParams);
    clearOnConfirmStoreChangeParams();
    updateUserStore(onConfirmStoreChangeParams?.newStore ?? store);
  };

  useEffect(() => {
    if (isValidPosition(activeCoordinates)) {
      searchNearbyRestaurants({
        location: activeCoordinates,
      });
    }
    // Triggers a search when the service mode change to keep the list in sync
  }, [activeCoordinates, searchNearbyRestaurants, serviceMode]);

  useEffect(() => {
    searchFavRestaurants(activeCoordinates);
  }, [activeCoordinates, searchFavRestaurants]);

  useEffect(() => {
    searchRecentRestaurants(activeCoordinates);
  }, [activeCoordinates, searchRecentRestaurants]);

  useRumPageView('store-locator', 'Store Locator');
  useEffect(() => {
    return () => {
      emitEvent(EventName.STORE_LOCATOR_UNMOUNT);
    };
  }, []);

  const onSearchBarValueChange = useCallback(
    (address: string | undefined | null) => {
      setManualAddress(address || '');
    },
    [setManualAddress]
  );

  return (
    <>
      <StoreLocatorView
        activeCoordinates={activeCoordinates}
        errorFavs={errorFavs}
        errorNearby={errorNearby}
        errorRecent={errorRecent}
        handleResetLocation={resetCoordsAndLoadCurrentPosition}
        searchingNearby={searchingNearby}
        searchingRecent={searchingRecent}
        searchingFavs={searchingFavs}
        searchNearbyRestaurants={searchNearbyRestaurants}
        storesFavs={storesFavs}
        storesNearby={storesNearby}
        storesRecent={storesRecent}
        rawIsSearching={rawIsSearching}
        // Map View Props
        onPressSearch={onPressSearch}
        // View Props
        currentView={currentView}
        onViewChange={updateStoreLocatorView}
        // Search Bar Props
        onAddressSelected={updateManualCoordinates}
        searchBarValue={manualAddress}
        onSearchBarValueChange={onSearchBarValueChange}
        shouldFocusSearchBar={!manualAddress && isPermissionGranted && isDesktop}
        // Service Mode Category Props
        hideServiceModeCategories={!orderingEnabled}
        selectedServiceModeCategory={serviceModeCategory}
        serviceModeCategoryOptions={serviceModeCategoryOptions}
        // @ts-expect-error TS(2322) FIXME: Type '(value: ServiceModeCategory) => void' is not... Remove this comment to see the full error message
        onServiceModeCategoryChange={onServiceModeCategoryChange}
        handleSelectStore={handleSelectStore}
        isInSupportForm={isInSupportForm}
        onDismissInSupportForm={onDismissInSupportForm}
        {...rest}
      />
      <AutoPromptLocationPermission />
      <ErrorDialog />
      <Dialog />
      {onConfirmStoreChangeParams?.unavailableCartEntries && (
        <RemoveUnavailableItemsModal
          onConfirmCb={handleOnConfirmCb}
          unavailableCartEntries={onConfirmStoreChangeParams?.unavailableCartEntries}
        />
      )}
      <StoreChangeDialog heading={formatMessage({ id: 'changeStores' })} />
    </>
  );
};

export default StoreLocator;
